import React, { useState, useEffect } from 'react';
import { MoreVertical, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatTimeRemaining } from '@/lib/dateFormat';
import { AdSlot } from '@/components/ads/AdSlot';
import { usePickemContestCardAd } from '@/hooks/usePickemContestCardAd';
import { Button } from '../ui/button';
import { ContestStartMeta } from '@/components/shared/ContestStartMeta';
import { ContestResultsMeta } from '@/components/shared/ContestResultsMeta';
import { LeaderBoardIcon } from '../icons/LeaderBoardIcon';
import { ViewResultsIcon } from '../icons/ViewResultsIcon';

type GameState = 'upcoming' | 'countdown' | 'live' | 'final';

interface GameCardProps {
  id: string;
  title: string;
  prize: string;
  state: GameState;
  gameDate: Date;
  finalScore?: { rank: number; points: number };
  hasSelections?: boolean;
  /** Dim card when contest is not open for entry (separate from live/final view-only). */
  isLocked?: boolean;
  isOpen?: boolean;
  totalPicks?: number;
  correctPicks?: number;
  currentPoints?: number;
  /** User prize won (Figma “My Prize”). */
  myPrize?: string;
  leagueLogoUrl?: string;
  totalEntries?: number;
  entryCost?: string;
  onEdit?: () => void;
  onPlay?: () => void;
  onView?: () => void;
  onViewLeaderboard?: () => void;
  onMenuClick?: () => void;
  /** Fired once when countdown reaches contest start (e.g. invalidate pickem queries). */
  onCountdownComplete?: () => void;
  className?: string;
}

const GameCard: React.FC<GameCardProps> = ({
  id,
  title,
  prize,
  state,
  gameDate,
  finalScore,
  hasSelections = false,
  isLocked = false,
  isOpen = true,
  totalPicks = 6,
  leagueLogoUrl: _leagueLogoUrl,
  totalEntries: _totalEntries,
  entryCost: _entryCost,
  currentPoints,
  myPrize,
  onEdit,
  onPlay,
  onView,
  onViewLeaderboard,
  onMenuClick,
  onCountdownComplete,
  className = '',
}) => {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const { adPlacement, shouldShowAd } = usePickemContestCardAd(id);

  useEffect(() => {
    if (state !== 'countdown') {
      return;
    }

    setTimeRemaining(formatTimeRemaining(gameDate));
    let completed = false;

    const interval = setInterval(() => {
      const now = new Date();
      const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;

      if (targetDate.getTime() <= now.getTime()) {
        clearInterval(interval);
        setTimeRemaining(t('game.time.startingSoon'));
        if (!completed) {
          completed = true;
          onCountdownComplete?.();
        }
      } else {
        setTimeRemaining(formatTimeRemaining(targetDate));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gameDate, state, t, onCountdownComplete]);

  const getStartDisplay = () => {
    switch (state) {
      case 'countdown':
        return timeRemaining || formatTimeRemaining(gameDate);
      case 'live':
        return t('game.status.live');
      case 'final':
        return t('game.status.complete');
      default: {
        const now = new Date();
        const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;
        const diff = targetDate.getTime() - now.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (days > 0) return `${days}-${days > 1 ? t('game.time.days') : t('game.time.day')}`;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours > 0) return `${hours} ${hours > 1 ? t('game.time.hours') : t('game.time.hour')}`;
        return t('game.time.soon');
      }
    }
  };

  const handleCardClick = () => {
    if (!isOpen) {
      return;
    }

    if (state === 'live' || state === 'final') {
      onView?.();
    } else if (hasSelections) {
      onEdit?.();
    } else {
      onPlay?.();
    }
  };

  const isLive = state === 'live';
  const isLiveOrFinal = isLive || state === 'final';

  const pointsDisplay = currentPoints ?? finalScore?.points ?? 0;
  const rankDisplay = finalScore?.rank ?? '—';
  const myPrizeDisplay = myPrize || '-';

  return (
    <div
      data-test-id={`game-card-${id}`}
      data-test-state={state}
      data-test-locked={isLocked}
      onClick={handleCardClick}
      className={`
        overflow-hidden
        ${isLive ? 'live-card-glow' : 'rounded-xl border border-border bg-surface'}
        ${isLocked ? 'opacity-60' : ''}
        ${
          !isOpen
            ? 'cursor-default opacity-50'
            : isLive
              ? 'cursor-pointer active:scale-[0.99]'
              : 'cursor-pointer hover:border-muted-foreground/40 active:scale-[0.99]'
        }
        transition-all duration-200
        ${className}
      `}
    >
      <div className="flex items-start justify-between gap-2 px-6 py-4">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <span
            data-test-id={`game-title-${id}`}
            className="font-sans text-sm font-extrabold capitalize leading-5 text-white"
          >
            {title}
          </span>

          {/* Live / Complete — Figma 10203:54634 / 10203:56675 */}
          {isLiveOrFinal && (
            <ContestResultsMeta
              variant={isLive ? 'live' : 'final'}
              totalPicks={totalPicks}
              points={pointsDisplay}
              rank={rankDisplay}
              myPrize={myPrizeDisplay}
              liveLabel={t('game.status.live')}
              completeLabel={t('game.status.complete')}
              testId={id}
            />
          )}

          {/* Pre / countdown */}
          {!isLiveOrFinal && (
            <>
              <ContestStartMeta value={getStartDisplay()} picksCount={totalPicks} />
              <div className="flex flex-wrap items-center gap-2 font-sans text-label-sm capitalize whitespace-nowrap">
                <span className="font-normal text-muted-foreground">
                  {t('game.labels.prizePool')}
                </span>
                <span data-test-id={`prize-${id}`} className="font-semibold text-success">
                  {prize}
                </span>
              </div>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onMenuClick?.();
          }}
          className="shrink-0 p-1 text-muted-foreground transition-colors hover:text-white"
          aria-label={t('a11y.moreOptions')}
        >
          <MoreVertical className="size-6" strokeWidth={2} />
        </button>
      </div>

      {shouldShowAd && adPlacement && (
        <div className="mb-0 px-4 pb-4">
          <AdSlot
            divId={`pickem-contest-card-${id}`}
            adPlacement={adPlacement}
            placement="pickem-contest-card"
            className="overflow-hidden rounded-lg"
          />
        </div>
      )}

      {isLiveOrFinal && (
        <div className="flex w-full gap-2 border-t border-border px-4 py-3">
          <Button
            variant="primary"
            shape="rounded"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onView?.();
            }}
            className="flex-1 whitespace-nowrap"
            data-test-id={`view-results-${id}`}
            showTrailingIcon={false}
          >
            {t('game.actions.viewResults')}
            <ViewResultsIcon />
          </Button>
          <Button
            variant="primary"
            shape="rounded"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onViewLeaderboard?.();
            }}
            className="flex-1 whitespace-nowrap"
            data-test-id={`view-leaderboard-${id}`}
            showTrailingIcon={false}
          >
            {t('game.actions.viewLeaderboard')}
            <LeaderBoardIcon />
          </Button>
        </div>
      )}

      {(state === 'countdown' || state === 'upcoming') && hasSelections && isOpen && (
        <div className="flex w-full justify-center border-t border-border px-4 py-3">
          <Button
            variant="primary"
            size="sm"
            onClick={e => {
              e.stopPropagation();
              onEdit?.();
            }}
            className="whitespace-nowrap px-6"
            data-test-id={`edit-picks-${id}`}
            showTrailingIcon={false}
          >
            {t('game.actions.editPicks')}
            <Pencil className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default GameCard;
